import { lazy, useEffect, useState, type ReactNode, useRef } from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { BootSplash } from "@/components/brand/scandi-loader";
import { ContentBoundary, isTransientError, PageSuspense, ReconnectingScreen } from "@/components/errors";
import { sessionKey, useSession } from "@/hooks/use-session";
import { AppLayout } from "@/components/layout/app-layout";
import { LoginPage } from "@/features/auth/login-page";
import { useDeckRedirect } from "@/features/auth/next-redirect";
import { NoAccessPage } from "@/features/auth/no-access-page";
import { briefPath } from "@/features/pipeline/brief-path";
import { LeaderboardPage } from "@/pages/leaderboard";
import { ScriptRedirect } from "@/features/storyboard/script-editor/script-editor-page";
import { Surface } from "@/components/surface";
import { preloadLikelyPages } from "@/lib/route-prefetch";
import { startPresence } from "@/lib/presence";

// Every lazily loaded page, shared with the prefetcher (`lib/route-prefetch.ts`) so both name the same chunks.
import {
  EditorDetailPage,
  CreativeDetailPage,
  TopVideosPage,
  SettingsPage,
  UsersPage,
  ApiKeysPage,
  AssignmentsPage,
  ConceptPage,
  ConceptsOverviewPage,
  ArtifactsPage,
  ArtifactViewerPage,
  MasterPage,
  ActivityPage,
  LibraryPage,
  PromptChatPage,
  SpendPage,
  TranslationsPage,
  ResourcesPage,
  IdeasRoutes,
  PresentationSummaryPage,
  LinePage,
  ShotRedirect,
  DeckPage,
  EditorLinePage,
  ClipEditorPage,
  AgentChatPage,
  AgentSessionsPage,
  StudioLayout,
  StudioHome,
  StudioChatPage,
  StudioJobsPage,
  StudioNewJobPage,
  StudioJobPage,
  StudioJobPresentationPage,
  AgentsAdminPage,
} from "@/route-pages";

// Dev only: the crash lab, every error and loading screen on demand. In a build
// `import.meta.env.DEV` is false, so neither this page nor its route is there.
const DevCrashPage = import.meta.env.DEV
  ? lazy(() => import("@/components/errors/dev-crash-page").then((m) => ({ default: m.DevCrashPage })))
  : null;

/** Every concept of every editor. A strategist's front door. */
const OVERVIEW = "/pipeline/conceptos";
/** An editor's own list of assigned concepts. */
const MY_WORK = "/pipeline/mi-trabajo";

/** How long away before the splash plays again on a plain load. */
const SPLASH_AWAY_MS = 4 * 60 * 60 * 1000;
const LAST_OPEN_KEY = "scandi.lastOpenAt";

function awayLongEnough(): boolean {
  try {
    const last = Number(window.localStorage.getItem(LAST_OPEN_KEY));
    return !last || Date.now() - last > SPLASH_AWAY_MS;
  } catch {
    return true;
  }
}

function markOpen(): void {
  try {
    window.localStorage.setItem(LAST_OPEN_KEY, String(Date.now()));
  } catch {
    /* storage blocked: the splash simply plays as before */
  }
}

export function App() {
  const { isLoading, isAuthenticated, isPending, refetch, role } = useSession();
  const queryClient = useQueryClient();

  // No answer to "who is signed in" because the API is restarting or the
  // network dropped. That is not being signed out, so not the login page: the
  // reconnecting screen asks again by itself and carries on when the API is
  // back. Remembered across its own retries, during which the session reads as
  // loading again, so the screen stays put rather than blinking.
  const unreachable = !isAuthenticated && isTransientError(queryClient.getQueryState(sessionKey)?.error);
  const [reconnecting, setReconnecting] = useState(false);
  if (unreachable && !reconnecting) setReconnecting(true);
  if (reconnecting && !unreachable && !isLoading) setReconnecting(false);

  // The splash greets someone arriving into the platform: straight after
  // signing in, or on a load after a while away (SPLASH_AWAY_MS since the
  // platform was last open). Never on an ordinary refresh, never in front of
  // the login page, and it plays to the end however fast the session answered.
  const [splashPlayed, setSplashPlayed] = useState(() => !awayLongEnough());
  const inside = isAuthenticated && !isPending;

  // Signing out arms it again for the next sign-in. Only a real sign-out
  // counts: while the session is still loading on a refresh it reads as
  // signed out too, and that must not re-arm the splash.
  const wasInside = useRef(false);
  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated) {
      wasInside.current = true;
    } else if (wasInside.current) {
      wasInside.current = false;
      setSplashPlayed(false);
    }
  }, [isAuthenticated, isLoading]);

  // Once inside, and the page has had its turn: the code of the pages this role usually opens next.
  const preloaded = useRef(false);
  useEffect(() => {
    if (!inside || preloaded.current) return;
    preloaded.current = true;
    preloadLikelyPages(role);
  }, [inside, role]);

  // "Last open" is stamped while the platform is in use, so a refresh finds it recent.
  useEffect(() => {
    if (!inside) return;
    markOpen();
    const onHide = () => markOpen();
    const tick = window.setInterval(markOpen, 60_000);
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onHide);
    return () => {
      window.clearInterval(tick);
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onHide);
    };
  }, [inside]);

  if (reconnecting && (unreachable || isLoading)) return <ReconnectingScreen retry={refetch} retrying={isLoading} />;

  // A single fast request; nothing is shown yet, since what comes next may be
  // the login page.
  if (isLoading) return null;

  return (
    <>
      <AppContent />
      {inside && !splashPlayed && <BootSplash onDone={() => setSplashPlayed(true)} />}
    </>
  );
}

/** Where an editor lands from a presentation-editor link: the concept it is about. */
function ToConcept() {
  const { ref } = useParams();
  return <Navigate to={ref ? briefPath(ref) : "/"} replace />;
}

function AppContent() {
  const { isAuthenticated, isPending, isAdmin, isRealAdmin, role, canUsePipeline, studio } = useSession();

  // Before anything is drawn, and before the early returns below, because a
  // hook cannot live behind one: somebody who followed a deck link while
  // signed out landed here with `?next=/p/<slug>/`, and now that they have a
  // session with a role they are owed the deck rather than the front page.
  useDeckRedirect(isAuthenticated && !isPending);
  // Every approved tab tells the server it is open and whether anyone is using it (the Activity page's presence).
  const beating = isAuthenticated && !isPending;
  useEffect(() => (beating ? startPresence() : undefined), [beating]);

  if (!isAuthenticated) return <LoginPage />;
  // Signed in, but no role yet. In place of the routes rather than as one of
  // them: every page behind it would only fetch data the API refuses, and the
  // URL is kept so the link they came in on still works once access arrives.
  if (isPending) return <NoAccessPage />;

  /**
   * Send anyone else home. This hides the page, and the API refuses the same
   * requests on its own - the two together are what make it admin-only.
   */
  const adminOnly = (page: ReactNode, fill = false) =>
    isAdmin ? lazyPage(page, fill) : <Navigate to="/" replace />;

  /**
   * A real admin, also while viewing as someone: for a page that must explain
   * itself under view-as rather than vanish. The page itself shows nothing but
   * that explanation until the admin is back in their own view, and the API
   * refuses its data under view-as anyway.
   */
  const realAdminOnly = (page: ReactNode, fill = false) => (isRealAdmin ? lazyPage(page, fill) : <Navigate to="/" replace />);

  /**
   * Whoever looks across editors: strategists and admins. This is the overview
   * of everybody's concepts, which an editor's account does not have.
   */
  const acrossEditors = (page: ReactNode) =>
    isAdmin || role === "strategist" ? lazyPage(page) : <Navigate to="/" replace />;

  /**
   * The studio's script chat: strategists and admins, and a user or editor with
   * the "Script chat" add-on. Everyone else is sent home; the API refuses them.
   */
  const anyStudio = (page: ReactNode) => (studio !== null ? page : <Navigate to="/" replace />);

  /**
   * The rest of the studio - its front page, the ad jobs - for the whole studio
   * only. A "Script chat" account lands in its chats instead, the one part of
   * the studio it has; the API refuses it the rest with `script_chat_only`.
   */
  const wholeStudio = (page: ReactNode) =>
    studio === "full" ? page : <Navigate to={studio === "script_chat" ? "/estudio/guion" : "/"} replace />;

  /**
   * The old deck register's link: an admin's. A strategist's lands on the
   * overview rather than on a tool they do not have; an editor is sent home.
   * This is what is offered, not what is allowed - the API decides that.
   */
  const adminOrOverview = (page: ReactNode) =>
    isAdmin ? lazyPage(page) : <Navigate to={role === "strategist" ? OVERVIEW : "/"} replace />;

  /**
   * The presentation editor: strategists and admins, the same people as
   * `acrossEditors`. An editor who follows a link here is sent to the concept
   * itself rather than home - it is the page about the same thing that they
   * do have - and the API refuses them the live storyboard anyway.
   */
  const strategistOrAdmin = (page: ReactNode) =>
    isAdmin || role === "strategist" ? lazyPage(page) : <ToConcept />;

  /**
   * A page whose code loads on the way in: the page's outline meanwhile (after a
   * moment, so a quick load shows nothing). `fill` for the full-height screens.
   */
  function lazyPage(page: ReactNode, fill = false) {
    return <PageSuspense fill={fill}>{page}</PageSuspense>;
  }

  /**
   * The pipeline's own pages - a concept, the resources - for everyone who
   * works in it. A results-only account is sent to the leaderboard; the API
   * refuses those requests for it anyway.
   */
  const pipelineOnly = (page: ReactNode, fill = false) =>
    canUsePipeline ? lazyPage(page, fill) : <Navigate to="/leaderboard" replace />;

  /**
   * The front door is the leaderboard for everyone: it is what the team opens
   * the platform to see. Each role's working page is one click away in the
   * sidebar - an editor's concepts, everyone else's overview.
   */
  const home = () => <Navigate to="/leaderboard" replace />;

  /** An editor's concepts. Anyone else has the team-wide views instead. */
  const myWork = () => {
    if (role === "editor") return lazyPage(<AssignmentsPage />);
    if (role === "strategist") return <Navigate to={OVERVIEW} replace />;
    if (isAdmin) return <Navigate to={OVERVIEW} replace />;
    return <Navigate to="/leaderboard" replace />;
  };

  return (
    <Routes>
      {/* The clip editor takes the whole window: its own top bar, no app sidebar. The API decides who may
          change what (an editor on their own working concept, strategists, admins); everyone else plays it. */}
      <Route path="pipeline/b/:ref/presentacion/editor" element={pipelineOnly(<Surface name="clip_editor"><ClipEditorPage /></Surface>, true)} />
      {/* An ad job's presentation takes the whole window, like a hosted deck: the deck and its own controls,
          no app sidebar, no studio bar. The job page opens it in a tab of its own. */}
      <Route path="estudio/trabajos/:jobId/presentacion" element={acrossEditors(lazyPage(<Surface name="job_page"><StudioJobPresentationPage /></Surface>, true))} />
      <Route element={<AppLayout />}>
        {/* A page that fails shows its error screen in the content area; the sidebar and top bar keep working. */}
        <Route element={<ContentBoundary />}>
          <Route index element={home()} />
          <Route path="leaderboard" element={<LeaderboardPage />} />
          <Route path="videos" element={lazyPage(<TopVideosPage />)} />
          <Route path="editors/:editorId" element={lazyPage(<EditorDetailPage />)} />
          <Route path="creatives/:slug" element={lazyPage(<CreativeDetailPage />)} />
          {/* The bare pipeline URL lands on each role's working page: an
              editor's concepts, everyone else's overview. */}
          <Route path="pipeline" element={<Navigate to={role === "editor" ? MY_WORK : OVERVIEW} replace />} />
          <Route path="pipeline/mi-trabajo" element={myWork()} />
          <Route path="pipeline/conceptos" element={acrossEditors(<ConceptsOverviewPage />)} />
          <Route path="pipeline/b/:ref" element={pipelineOnly(<Surface name="concept_page"><ConceptPage /></Surface>)} />
          {/* A concept's presentation editor. The deck is every pipeline role's:
              the API decides whether an editor may see it (theirs, released). */}
          {/* An editor reads the same script view, read-only; its lines open their own line view. */}
          <Route
            path="pipeline/b/:ref/presentacion"
            element={<Surface name={role === "editor" ? "editor_view" : "presentation_editor"}>{role === "editor" ? pipelineOnly(<PresentationSummaryPage />) : strategistOrAdmin(<PresentationSummaryPage />)}</Surface>}
          />
          {/* The script editor was a page of its own; it is the presentation's edit mode now. */}
          <Route path="pipeline/b/:ref/presentacion/guion" element={<ScriptRedirect />} />
          <Route
            path="pipeline/b/:ref/presentacion/l/:lineKey"
            element={role === "editor" ? pipelineOnly(<Surface name="editor_view"><EditorLinePage /></Surface>) : strategistOrAdmin(<Surface name="line_page"><LinePage /></Surface>)}
          />
          {/* There is no shot page: its old address opens the shot's edit mode on its line page. */}
          <Route
            path="pipeline/b/:ref/presentacion/l/:lineKey/s/:shotKey"
            element={role === "editor" ? pipelineOnly(<ShotRedirect />) : strategistOrAdmin(<ShotRedirect />)}
          />
          <Route path="pipeline/b/:ref/deck" element={pipelineOnly(<Surface name="editor_view"><DeckPage /></Surface>, true)} />
          {/* Retired pages - the brief composer, the review queues and the
              board. Bookmarks and old notification links land on each role's
              pipeline page; an old composer link opens its concept. */}
          <Route path="pipeline/escribir" element={<Navigate to="/pipeline" replace />} />
          <Route path="pipeline/revision" element={<Navigate to="/pipeline" replace />} />
          <Route path="pipeline/board" element={<Navigate to="/pipeline" replace />} />
          <Route path="pipeline/b/:ref/editar" element={<ToConcept />} />
          {/* The decks themselves are served by the API at /p/<slug>/, not by
              this router. Their register grew into Artifacts, an admin's page
              (an editor reads the one deck on their concept); an old link lands there. */}
          <Route path="pipeline/presentaciones" element={adminOrOverview(<Navigate to="/artefactos" replace />)} />
          <Route path="artefactos" element={realAdminOnly(<ArtifactsPage />)} />
          <Route path="artefactos/:artifactId" element={realAdminOnly(<ArtifactViewerPage />, true)} />
          {/* The team's working links: the same list for every role. The old
              library lived at /pipeline/library, and bookmarks to it still land. */}
          <Route path="recursos" element={pipelineOnly(<ResourcesPage />)} />
          {/* Ideas: every pipeline role suggests; strategists and admins review (the API decides). */}
          <Route path="ideas/*" element={pipelineOnly(<IdeasRoutes />)} />
          <Route path="pipeline/library" element={<Navigate to="/recursos" replace />} />
          {/* The studio, strategists' and admins'; a user or editor with the "Script chat"
              add-on has the chat and nothing else of it (the API refuses everyone else, and the
              add-on everything but the chat). The script chat and one job are full height
              (app-layout's `fills`); the surfaces name where a write was made, for the change ledger. */}
          <Route path="estudio" element={anyStudio(lazyPage(<StudioLayout />))}>
            <Route index element={wholeStudio(lazyPage(<StudioHome />))} />
            <Route path="guion" element={lazyPage(<Surface name="studio_chat"><StudioChatPage /></Surface>, true)} />
            <Route path="guion/:sessionId" element={lazyPage(<Surface name="studio_chat"><StudioChatPage /></Surface>, true)} />
            <Route path="trabajos" element={wholeStudio(lazyPage(<StudioJobsPage />))} />
            <Route path="trabajos/nuevo" element={wholeStudio(lazyPage(<StudioNewJobPage />))} />
            <Route path="trabajos/:jobId" element={wholeStudio(lazyPage(<Surface name="job_page"><StudioJobPage /></Surface>, true))} />
          </Route>
          {/* Open to every signed-in role. The coach's own config stays admin-only. */}
          <Route path="coach" element={lazyPage(<AgentChatPage />, true)} />
          {/* AI generation's product pictures and voices: strategists and admins. */}
          {/* Product pictures are everyone's who generates, editors included; the voices are not. */}
          <Route path="biblioteca" element={isAdmin || role === "strategist" || role === "editor" ? lazyPage(<LibraryPage />) : <Navigate to="/" replace />} />
          <Route path="biblioteca/voces" element={acrossEditors(<LibraryPage />)} />
          {/* The prompt chat's team memory and settings: strategists and admins (editors change memory through the chat). */}
          <Route path="admin/prompt-chat" element={acrossEditors(<PromptChatPage />)} />
          <Route path="coach/settings" element={lazyPage(<SettingsPage />)} />
          <Route path="coach/sessions" element={adminOnly(<AgentSessionsPage />, true)} />
          <Route
            path="coach/sessions/:threadId"
            element={adminOnly(<AgentSessionsPage />, true)}
          />
          {/* Every setting, one hub; each section decides who sees it (features/settings/registry.ts). The old
              settings addresses (account, admin, coach/settings, admin/notificaciones) load the hub too, which
              sends them on to their section (features/settings/legacy.ts), so the redirects cost the shell nothing. */}
          <Route path="settings" element={lazyPage(<SettingsPage />)} />
          <Route path="settings/:section" element={lazyPage(<SettingsPage />)} />
          <Route path="account" element={lazyPage(<SettingsPage />)} />
          <Route path="admin" element={lazyPage(<SettingsPage />)} />
          <Route path="admin/users" element={adminOnly(<UsersPage />)} />
          <Route path="admin/api-keys" element={adminOnly(<ApiKeysPage />)} />
          <Route path="admin/actividad" element={adminOnly(<ActivityPage />, true)} />
          <Route path="admin/gasto" element={realAdminOnly(<SpendPage />)} />
          <Route path="admin/notificaciones" element={lazyPage(<SettingsPage />)} />
          <Route path="admin/traducciones" element={adminOnly(<TranslationsPage />)} />
          {/* The cloud agents: spend, audit, settings, captured knowledge, health. */}
          <Route path="admin/agentes" element={realAdminOnly(<AgentsAdminPage />)} />
          <Route path="master" element={realAdminOnly(<MasterPage />, true)} />
          <Route path="master/:sid" element={realAdminOnly(<MasterPage />, true)} />
          {/* Old admin URLs, kept so a bookmark still lands somewhere useful. */}
          <Route path="admin/chat" element={<Navigate to="/coach" replace />} />
          <Route path="admin/chat/settings" element={lazyPage(<SettingsPage />)} />
          {DevCrashPage && <Route path="__dev/crash/*" element={lazyPage(<DevCrashPage />)} />}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Route>
    </Routes>
  );
}
